import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

/**
 * Formats time in milliseconds to a M:SS string (e.g., "1:05", "0:30").
 * @param {number} timeMs - Time in milliseconds.
 * @returns {string} Formatted time string.
 */
function formatTime(timeMs) {
  // Ensure time is non-negative
  const positiveTimeMs = Math.max(0, timeMs);

  // Calculate total seconds, rounding down.
  const totalSeconds = Math.floor(positiveTimeMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // Pad seconds with a leading zero if needed
  const paddedSeconds = String(seconds).padStart(2, '0');

  // Return in M:SS format
  return `${minutes}:${paddedSeconds}`;
}

// Default countdown duration (60 seconds)
const DEFAULT_DURATION = 60 * 1000;


const TimerComponent = forwardRef(({ duration = DEFAULT_DURATION, onComplete }, ref) => {

  const [isRunning, setIsRunning] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [displayTime, setDisplayTime] = useState(duration); 
  const [isVisible, setIsVisible] = useState(false);


  const startTimeRef = useRef(null); 
  const animationFrameRef = useRef(null); 

  /**
   * The main update loop using requestAnimationFrame for smooth UI updates.
   * Calculates the remaining time and stops the timer when it reaches zero.
   * Calls the onComplete callback when the timer finishes.
   */
  const update = useCallback(() => {

    if (!isRunning || !startTimeRef.current) return;

    const now = performance.now();

    const currentTotalElapsedTime = elapsedTime + (now - startTimeRef.current);

    const remainingTime = duration - currentTotalElapsedTime;

    if (remainingTime <= 0) {
      setIsRunning(false); 
      setElapsedTime(duration);
      setDisplayTime(0); 
      startTimeRef.current = null; 
     
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      if (typeof onComplete === 'function') {
        onComplete();
      }

      return;
    }


    setDisplayTime(remainingTime);

    // Request the next frame to continue the loop
    animationFrameRef.current = requestAnimationFrame(update);
  }, [isRunning, elapsedTime, duration, onComplete]); // Added onComplete to dependencies

  /**
   * Effect to manage the requestAnimationFrame loop.
   */
  useEffect(() => {
    if (isRunning) {
      animationFrameRef.current = requestAnimationFrame(update);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRunning, update]);

  // --- Timer Control Functions ---

  /**
   * Starts the countdown timer from the beginning (or resumes if paused at zero).
   * Resets elapsed time, sets the display to the full duration,
   * records the start time, and sets isRunning to true.
   */
  const handleStart = useCallback(() => {
    // Prevent starting if already running
    if (isRunning) return;

    // Reset elapsed time for a fresh countdown
    setElapsedTime(0);
    // Set display time to the full duration immediately
    setDisplayTime(duration);
    // Record the exact moment the timer starts
    startTimeRef.current = performance.now();
    // Set running state to true (this triggers the useEffect to start the loop)
    setIsRunning(true);

  }, [isRunning, duration]); // Dependencies: isRunning, duration

  /**
   * Pauses the timer. Calculates elapsed time since the last start/resume,
   * adds it to the total elapsedTime, and sets isRunning to false.
   */
  const handlePause = useCallback(() => {
    if (isRunning && startTimeRef.current) {
      const now = performance.now();
      // Calculate time elapsed during this running interval and add to total elapsed
      setElapsedTime(prev => prev + (now - startTimeRef.current));
      // Set running state to false (this triggers the useEffect to stop the loop)
      setIsRunning(false);
      // Keep startTimeRef for potential resume calculation
    }
  }, [isRunning]); // Dependency: isRunning

  /**
   * Resumes the timer from a paused state. Records the new start time
   * and sets isRunning to true. Does nothing if already running or finished.
   */
  const handleResume = useCallback(() => {
     // Only resume if paused and not already finished (displayTime > 0)
     if (!isRunning && displayTime > 0) {
      // Record the exact moment the timer resumes
      startTimeRef.current = performance.now();
      // Set running state to true (triggers the useEffect to start the loop)
      setIsRunning(true);
     }
  }, [isRunning, displayTime]); // Dependencies: isRunning, displayTime

  /**
   * Resets the timer completely. Stops the timer, clears elapsed time,
   * resets display time back to the full initial duration.
   * Also hides the timer display by default on reset.
   */
   const handleReset = useCallback(() => {
    setIsRunning(false);
    setElapsedTime(0);
    setDisplayTime(duration); // Reset display to the initial duration
    setIsVisible(false); // Hide on reset
    startTimeRef.current = null;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, [duration]); // Dependency: duration

  /**
   * Allows rendering of the time.
   * Sets the visibility state to true.
   */
  const handleShow = useCallback(() => {
    setIsVisible(true);
  }, []); // No dependencies needed

  /**
   * Hides rendering of the time.
   * Sets the visibility state to false.
   */
  const handleHide = useCallback(() => {
    setIsVisible(false);
  }, []); // No dependencies needed


  // --- Expose Control Functions to Parent via Ref ---

  /**
   * Calculates the current remaining time in milliseconds.
   * @returns {number} Remaining time in milliseconds.
   */
  const calculateRemainingTimeMs = () => {
    if (isRunning && startTimeRef.current) {
      const currentTotalElapsedTime = elapsedTime + (performance.now() - startTimeRef.current);
      return Math.max(0, duration - currentTotalElapsedTime); // Ensure non-negative
    } else {
      // If paused or stopped, calculate based on stored elapsedTime
      return Math.max(0, duration - elapsedTime);
    }
  };

  /**
   * useImperativeHandle hook exposes specific functions to the parent component.
   */
  useImperativeHandle(ref, () => ({
    start: handleStart,
    pause: handlePause,
    resume: handleResume,
    reset: handleReset,
    show: handleShow, // Keep your exposed show function
    hide: handleHide, // Keep your exposed hide function
    // Expose remaining time calculation in milliseconds
    getCurrentRemainingTime: calculateRemainingTimeMs,
    // Expose remaining time calculation rounded to the nearest second
    getCurrentRemainingSeconds: () => {
      const remainingMs = calculateRemainingTimeMs();
      // Use Math.ceil to ensure 1 second shows until it truly hits 0ms
      return Math.ceil(remainingMs / 1000);
    },
    getDisplayTime: () => formatTime(displayTime) // Expose the state used for display
  }));

  // --- Render the Timer Display ---
  // NEW: Determine the color based on the displayTime
  const timerColor = displayTime <= 0 ? '#E02020' : undefined; // Use default/inherited color otherwise

  return (
    <div className="timer-display">
      {/* Conditionally render the h1 based on the isVisible state */}
      {isVisible && (
        // NEW: Apply the conditional style for text color
        <h1 style={{ color: timerColor }}>
          {formatTime(displayTime)}
        </h1>
      )}
      {/* If you want something to show when hidden, you could add an else condition here */}
      {/* {!isVisible && <p>(Timer is hidden)</p>} */}
    </div>
  );
}); // Close the forwardRef

export default TimerComponent;
